import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { IntegrationErrorPanel } from '../../components/integrations/IntegrationBoundary';
import { Cable } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { fetchMapping, type IntegrationActivity } from '../../api/dataApi';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { AppCard } from '../../components/ui/AppCard';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { FormSection } from '../../components/ui/FormSection';
import { EmptyState } from '../../components/ui/EmptyState';
import { deriveIntegrationView, integrationStateCopy } from '../../lib/integrationView';
import { findConnectorForMapping } from '../../lib/connectors';
import { StatePill } from './IntegrationsListScreen';
import { MappingVisualPreview } from './MappingVisualPreview';
import { AskSourceCard, ASK_PROVIDERS } from '../../components/integrations/AskSourceCard';
import { MappingVisualEditor, PREVIEWABLE } from '../../components/integrations/MappingVisualEditor';
import { LiveMappingCard } from '../../components/integrations/LiveMappingCard';
import { ActivitySection } from '../../components/integrations/ActivitySection';
import { RuntimeCard } from '../../components/integrations/RuntimeCard';
import { VersionsSection } from '../../components/integrations/VersionsSection';
import { CredentialsCard, CREDENTIAL_SOURCE_TYPES } from '../../components/integrations/CredentialsCard';
import { TargetUrlCard } from '../../components/integrations/TargetUrlCard';
import type { Mapping } from '../../types';

// Integration detail shell (roadmap #9 Phase 1 slice 4). Overview / Mapping
// / Settings, stacked as sections rather than a tab-switcher - this
// codebase has no Tabs component anywhere (see ChannelDetailsScreen), so a
// new tab-switching pattern would be a speculative abstraction this one
// screen doesn't need.
//
// Activity (Phase 2): derived health, counters, error groups and the run
// list, from backend/internal/integrationrun. Owner-only - the endpoint
// 403s anyone else. Phase 1 omitted this section entirely because no run
// record existed (audit §6.6.2, R7); now one does, so it is real, not
// decorative.
//
// The Mapping and Settings sections both point at the existing
// MappingManageScreen (via /integrations/:id/mapping) rather than
// reimplementing spec/title/description/isPublic editing here - that
// screen already owns validation, save, delete, and credential rotation
// for this exact document, and forking a second editing surface over the
// same fields would duplicate rather than reuse it (see that screen's
// header comment for why it's one form, not split by field group).
//
// The Mapping section renders MappingVisualEditor for the owner (three-pane
// source / builder / preview, saving to a DRAFT that Versions publishes) and
// the read-only MappingVisualPreview for everyone else.
export function IntegrationDetailScreen() {
  const { mappings, isMappingOwner, syncMappings } = useStore();
  const { integrationId } = useParams();
  const [draftSaves, setDraftSaves] = useState(0);
  // docs/live-mapping-task.md: Simple (LiveMappingCard) is the default view
  // for the Mapping section; its "more options" button reveals the
  // unmodified three-pane MappingVisualEditor in place, for anything Simple
  // doesn't cover (transforms, casts, multiple outputs, raw JSON).
  const [advanced, setAdvanced] = useState(false);
  const [activity, setActivity] = useState<IntegrationActivity | null>(null);

  const local = mappings?.find((m) => m?.id === integrationId || m?.nickname === integrationId);
  const [fetched, setFetched] = useState<Mapping | null>(null);
  const [loading, setLoading] = useState(!local);
  const [loadError, setLoadError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (local || !integrationId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    fetchMapping(integrationId)
      .then((m) => {
        if (!cancelled) setFetched(m);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Unable to load this integration.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [integrationId, local, attempt]);

  const mapping = local ?? fetched;
  useEffect(() => {
    setActivity(null);
  }, [mapping?.nickname]);
  const view = useMemo(
    () => (mapping ? deriveIntegrationView(mapping, { hasDelivered: (activity?.stats.totalRuns ?? 0) > 0 }) : null),
    [mapping, activity]
  );
  const connector = useMemo(() => {
    if (!mapping) return undefined;
    const provider = typeof mapping.spec?.provider === 'string' ? mapping.spec.provider : undefined;
    return findConnectorForMapping(mapping.sourceType, provider);
  }, [mapping]);

  return (
    <div className="flex flex-col gap-4">
      <ScreenHeader
        title={mapping?.title || mapping?.nickname || 'Integration'}
        backTo="/integrations"
        action={
          mapping && isMappingOwner(mapping) ? (
            <Link to={`/integrations/${mapping.id}/mapping`}>
              <Button variant="bordered">Edit</Button>
            </Link>
          ) : undefined
        }
      />

      {!mapping || !view ? (
        loading ? (
          <div className="py-10 text-center text-sm text-black/40 dark:text-white/40">Loading…</div>
        ) : loadError ? (
          <IntegrationErrorPanel title="Could not load this integration" message={loadError} onRetry={() => setAttempt((n) => n + 1)} />
        ) : (
          <EmptyState icon={Cable} title="Integration not found" subtitle="It may be private or no longer exist." />
        )
      ) : (
        <>
          <FormSection title="Overview" collapsible id="integration-overview">
            <AppCard className="flex flex-col gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{connector?.name || mapping.sourceType || 'Custom'}</span>
                {mapping.isPublic && (
                  <span className="rounded-full bg-blue-600/15 text-blue-600 dark:text-blue-400 text-[11px] font-medium px-2 py-0.5">
                    Public
                  </span>
                )}
                <div className="flex-1" />
                <StatePill state={view.state} />
              </div>
              <p className="text-xs text-black/45 dark:text-white/45 m-0">{integrationStateCopy[view.state].description}</p>
              <div className="flex flex-col gap-1 text-sm pt-1">
                <span>
                  Destination:{' '}
                  <span className="font-mono">{view.destinationChannel ? `#${view.destinationChannel}` : 'not set'}</span>
                </span>
                {mapping.description && <span className="text-black/60 dark:text-white/60">{mapping.description}</span>}
              </div>
              {isMappingOwner(mapping) && <RuntimeCard nickname={mapping.nickname} />}
            </AppCard>
          </FormSection>

          {isMappingOwner(mapping) && (
            <FormSection title="Activity" collapsible id="integration-activity">
              <ActivitySection
                nickname={mapping.nickname}
                isWebhook={mapping.sourceType === 'webhook'}
                onActivityLoaded={setActivity}
              />
            </FormSection>
          )}

          <FormSection title="Mapping" collapsible id="integration-mapping">
            {isMappingOwner(mapping) && ASK_PROVIDERS.includes(String(mapping.spec?.provider ?? '')) && (
              <AskSourceCard key={mapping.id} mapping={mapping} />
            )}
            {isMappingOwner(mapping) ? (
              advanced || !PREVIEWABLE.includes(mapping.sourceType.trim().toLowerCase()) ? (
                <MappingVisualEditor mapping={mapping} onDraftSaved={() => setDraftSaves((n) => n + 1)} />
              ) : (
                <LiveMappingCard mapping={mapping} onOpenAdvanced={() => setAdvanced(true)} onChanged={() => void syncMappings()} />
              )
            ) : (
              <MappingVisualPreview mapping={mapping} />
            )}
            {isMappingOwner(mapping) && advanced && PREVIEWABLE.includes(mapping.sourceType.trim().toLowerCase()) && (
              <Button variant="bordered" className="self-start" onClick={() => setAdvanced(false)}>
                Back to overview
              </Button>
            )}
            <details>
              <summary className="text-xs text-black/45 dark:text-white/45 cursor-pointer select-none">
                Raw spec (JSON)
              </summary>
              <pre className="mt-2 rounded-2xl border border-black/[0.06] dark:border-white/[0.08] bg-white/70 dark:bg-white/[0.06] p-4 overflow-x-auto text-[13px] font-mono leading-relaxed m-0">
                {JSON.stringify(mapping.spec ?? {}, null, 2)}
              </pre>
            </details>
            <p className="text-xs text-black/45 dark:text-white/45 -mt-1">
              Edit visually above and save a draft, then publish it under Versions - or edit the raw JSON in the Advanced editor.
            </p>
            {isMappingOwner(mapping) && (
              <Link to={`/integrations/${mapping.id}/mapping`} className="self-start">
                <Button variant="secondary">Open Advanced editor</Button>
              </Link>
            )}
          </FormSection>

          {isMappingOwner(mapping) && (
            <FormSection title="Versions" collapsible id="integration-versions">
              <VersionsSection mapping={mapping} reloadKey={draftSaves} onChanged={() => void syncMappings()} />
            </FormSection>
          )}

          <FormSection title="Settings" collapsible id="integration-settings">
            <div className="flex flex-col gap-2 text-sm">
              <span>
                Nickname: <span className="font-mono text-black/60 dark:text-white/60">{mapping.nickname}</span>
              </span>
              <span>
                Source type: <span className="font-mono text-black/60 dark:text-white/60">{mapping.sourceType || '—'}</span>
              </span>
              <span>
                Visibility: <span className="text-black/60 dark:text-white/60">{mapping.isPublic ? 'Public' : 'Private'}</span>
              </span>
              {mapping.ingestTokenSet !== undefined && (
                <span>
                  Ingest token:{' '}
                  <Badge>{mapping.ingestTokenSet ? 'Configured' : 'Not configured'}</Badge>
                </span>
              )}
              {mapping.webhookSecret !== undefined && (
                <span>
                  Webhook secret: <Badge>{mapping.webhookSecret ? 'Configured' : 'Not configured'}</Badge>
                </span>
              )}
            </div>
            {isMappingOwner(mapping) && <TargetUrlCard mapping={mapping} />}
            {isMappingOwner(mapping) && CREDENTIAL_SOURCE_TYPES.includes(mapping.sourceType) && (
              <CredentialsCard nickname={mapping.nickname} />
            )}
            {isMappingOwner(mapping) ? (
              <p className="text-xs text-black/45 dark:text-white/45">
                Renaming, changing visibility, rotating credentials, or deleting this integration all happen in the{' '}
                <Link to={`/integrations/${mapping.id}/mapping`} className="underline">
                  Advanced editor
                </Link>
                .
              </p>
            ) : (
              <p className="text-xs text-black/45 dark:text-white/45">Only the owner can change these settings.</p>
            )}
          </FormSection>
        </>
      )}
    </div>
  );
}
